import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { subscriptionsActions } from '$store/subscriptions/index';
import { Api } from '$api';
import { batchActions } from '$store';
import { mainActions, mainSelector } from '$store/main';
import { toastActions } from '$store/toast';
import router from '$navigation/router';
import { OTHER, PAY_FORM } from '$shared/constants/pages';
import { SubscriptionDialogAction } from '$store/subscriptions/interface';
import { SubscriptionType } from '$store/models';
import { t } from '$translation';
import { paySaga } from '$store/pay/sagas';
import { PayMethod } from '$store/pay/interface';
import { payActions } from '$store/pay';
import { balanceActions } from '$store/balance';

function* loadSubscriptionsWorker() {
  try {
    const data = yield call(Api.get, '/balance/subscriptions');
    yield put(
      batchActions(
        subscriptionsActions.setSubscriptions(data.subscriptions),
        mainActions.setUsers(data.users),
      ),
    );
  } catch (e) {
    yield put(subscriptionsActions.setFailed());
  }
}

function* subscriptionDialogWorker(action: SubscriptionDialogAction) {
  const { price: amount, categoryId, authorId } = action.payload;
  const { users } = yield select(mainSelector);
  const user = authorId ? users[authorId] : null;

  const { method, options } = yield paySaga({
    amount,
    title: t(
      authorId ? 'subscription_author_payment_title' : 'subscription_payment_title',
    ),
    availableMethods: [
      PayMethod.BALANCE,
      PayMethod.CRYPTOPAY,
      PayMethod.CARD,
      PayMethod.TONKEEPER,
    ],
    description: authorId
      ? t('subscription_payment_description_author', {
          amount,
          name: user ? user.firstName + ' ' + user.lastName : '',
        })
      : t(
          categoryId === SubscriptionType.Media
            ? 'subscription_payment_description_media'
            : 'subscription_payment_description_adult',
          {
            amount,
          },
        ),
    excludeFiat: user ? !user.verified : false,
  });

  if (method === PayMethod.BALANCE) {
    try {
      yield put(toastActions.setToastLoading());

      yield call(Api.post, '/balance/subscription/buy', {
        categoryId,
      });
    } catch (e) {
      if (e.code === 'insufficient_funds') {
        yield put(
          toastActions.setToastFail(t('top_up_balance_from_ton_insufficient_funds')),
        );

        if (categoryId === 2) {
          yield put(
            balanceActions.setTopUpTonActive({
              topUpActive: true,
              requireTopUpAmount: e.params.amount,
            }),
          );
        } else {
          // @ts-ignore
          yield call(router.navigate, OTHER, { w: 'top_up' });
        }

        return;
      }

      yield put(batchActions(toastActions.hideToast()));
      yield put(toastActions.setToastFail(e.message));
    }
  } else if (method === PayMethod.CARD) {
    try {
      yield put(toastActions.setToastLoading());

      let url = '/pay/subscription/';
      if (authorId) {
        url = `/pay/subscription/author/${authorId}`;
      } else if (categoryId) {
        url = `/pay/subscription/category/${categoryId}`;
      }

      const { status, decryptKey, token } = yield call(Api.post, url, {
        skipAutoPay: !!options.skipAutoPay,
      });

      if (status === 'sent_payment') {
        yield put(
          batchActions(
            mainActions.setDecryptKey({
              id: authorId ? authorId : categoryId ? -categoryId : SubscriptionType.Media,
              decryptKey,
            }),
            toastActions.setToastLoading(t('toast_payment_processing')),
          ),
        );
      } else {
        yield put(batchActions(toastActions.hideToast()));
        // @ts-ignore
        yield call(router.navigate, PAY_FORM, { token });
      }
    } catch (e) {
      yield put(batchActions(toastActions.hideToast()));
      yield put(toastActions.setToastFail(e.message));
    }
  } else if (method === PayMethod.CRYPTOPAY) {
    try {
      const { payUrl, invoiceId } = yield call(Api.post, '/cryptopay/subscription', {
        authorId: authorId,
        currency: options.currency,
        category: categoryId,
      });
      yield put(payActions.setPayUrl(payUrl));
      yield put(payActions.setInvoiceId(invoiceId));
    } catch (e) {
      yield put(toastActions.setToastFail(e.message));
    }
  } else if (method === PayMethod.TONKEEPER) {
    try {
      yield put(toastActions.setToastLoading());
      const { invoiceUrl } = yield call(
        Api.post,
        authorId && authorId > 0
          ? `/balance/create_invoice/author/${authorId}`
          : `/balance/create_invoice/category/${categoryId}`,
      );
      yield put(batchActions(toastActions.hideToast(), payActions.setPayUrl(invoiceUrl)));
    } catch (e) {
      yield put(toastActions.setToastFail(e.message));
    }
  }
}

export function* subscriptionsSaga() {
  yield all([
    takeLatest(subscriptionsActions.loadSubscriptions, loadSubscriptionsWorker),
    takeLatest(subscriptionsActions.subscriptionDialog, subscriptionDialogWorker),
  ]);
}
